import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useDebounce } from "use-debounce";

import { CustomTextInput } from "../common/inputFields/customTextInput";
import { CustomMultiSelect } from "../common/inputFields/customMultiSelect";
import {
  DealCompDateFilter,
  DATE_FILTER_MONTH_SET,
} from "./DealCompDateFilter";
import {
  allSalesCompSourcesSelected,
  isSalesCompSourceId,
  SALES_COMP_SOURCE_IDS,
  SALES_COMP_SOURCE_OPTIONS,
} from "@/constants/salesCompSources";
import "@/assets/css/Button.css";

/** Keys sent to comps filter POST body; empty inputs use explicit `null` to clear saved values. */
export type DealCompsQueryParams = Record<
  string,
  string | number | boolean | string[] | null
>;

export interface DealCompFilterProps {
  /** Area unit label for sales area min/max (deal country; no conversion). */
  salesAreaUnitLabel: "sq ft" | "sqm";
  /** Fired after debounce whenever built query params or validation state changes. */
  onParamsChange: (
    params: DealCompsQueryParams,
    hasValidationErrors: boolean,
  ) => void;
  /** Applied comps query (e.g. from parent state); used to restore inputs after remount (fullscreen). */
  queryParams: DealCompsQueryParams;
  /** Called when debounced field validation has user-visible errors (any invalid field). */
  onUserErrorsChange?: (hasErrors: boolean) => void;
  /** Parent sets `true` on "Clear Filters"; filter resets draft then sets back to `false`. */
  clearFiltersRequested: boolean;
  setClearFiltersRequested: Dispatch<SetStateAction<boolean>>;
  /** Persists cleared filters on the server and resets local draft via `clearFiltersRequested`. */
  onClearFiltersClick: () => void;
}

const RADIUS_KM_MIN = 1;
const RADIUS_KM_MAX = 20000;
const LOAN_AMOUNT_MIN_FLOOR = 1_000_000;
const LOAN_AMOUNT_MIN_CEIL = 120_000_000;
const LIMIT_PER_TYPE_MIN = 1;
const LIMIT_PER_TYPE_MAX = 2000;
/** Max length for `sales_vendor` / `sales_purchaser` (API max_length=500). */
const SALES_NAME_FILTER_MAX_LENGTH = 500;
/** Delay (ms) before debounced filter values drive API params and validation. */
const FILTER_DEBOUNCE_MS = 400;

/** Uniform width for filter fields in the General group and comp fieldsets. */
const FIELD_INPUT_WIDTH = "140px";

function fieldBoxSxForWidth(width: string) {
  return {
    width,
    flex: `0 0 ${width}`,
    minWidth: width,
    maxWidth: width,
  } as const;
}

const fieldBoxSx = fieldBoxSxForWidth(FIELD_INPUT_WIDTH);

const fieldColumnSx = {
  ...fieldBoxSx,
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
} as const;

/** Fields flow down at most two rows, then add columns (keeps groups compact). */
const filterFieldsGridSx = {
  display: "grid",
  gridTemplateRows: "repeat(2, auto)",
  gridAutoFlow: "column",
  gridAutoColumns: "max-content",
  alignItems: "stretch",
  columnGap: { xs: 1.5, sm: 2 },
  rowGap: { xs: 1.5, sm: 2 },
} as const;

const filterGroupsRowSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: 1.5,
  alignItems: "flex-start",
  width: "100%",
} as const;

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="fieldset"
      sx={{
        m: 0,
        p: 1.25,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        flex: "0 0 auto",
        width: "auto",
        maxWidth: "100%",
      }}
    >
      <Typography
        component="legend"
        sx={{
          px: 0.75,
          fontSize: "0.75rem",
          fontWeight: 800,
          lineHeight: 1.2,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "text.secondary",
          fontFamily: "Helvetica Neue, sans-serif",
        }}
      >
        {label}
      </Typography>
      <Box sx={filterFieldsGridSx}>{children}</Box>
    </Box>
  );
}

/** Same red as `CustomTextInput` when `redOutline` (invalid). */
const FILTER_FIELD_ERROR_COLOR = "#BA2C2C";

const fieldErrorSx = {
  color: `${FILTER_FIELD_ERROR_COLOR} !important`,
  fontSize: "0.65rem",
  lineHeight: 1.25,
  mt: 0.25,
  display: "block",
} as const;

function FieldError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <Typography component="span" sx={fieldErrorSx}>
      Input must be: {message}
    </Typography>
  );
}

function parsePositiveFloat(raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n)) return null;
  return n;
}

function parsePositiveInt(raw: string): number | null {
  if (raw.trim() === "") return null;
  if (!/^\d+$/.test(raw.trim())) return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return null;
  return n;
}

function parseNonNegativeFloat(raw: string): number | null {
  if (raw.trim() === "") return null;
  const n = Number.parseFloat(raw);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function salesSourcesFromQuery(q: DealCompsQueryParams): string[] {
  const raw = q.sales_sources;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.filter(
      (item): item is string =>
        typeof item === "string" && isSalesCompSourceId(item),
    );
  }
  return [...SALES_COMP_SOURCE_IDS];
}

type FilterDraft = {
  radiusKm: string;
  monthsBack: string;
  loanAmountMin: string;
  loanAmountMax: string;
  salesPriceMin: string;
  salesPriceMax: string;
  salesAreaMin: string;
  salesAreaMax: string;
  salesRequireYield: boolean;
  salesSources: string[];
  salesVendor: string;
  salesPurchaser: string;
  leaseTotalRentMin: string;
  leaseTotalRentMax: string;
  leaseAreaMin: string;
  leaseAreaMax: string;
  limitPerType: string;
};

function monthsBackSavedInQuery(q: DealCompsQueryParams): boolean {
  const v = q.months_back;
  if (v === null || v === undefined || v === "") return false;
  const n = typeof v === "number" ? v : Number.parseInt(String(v), 10);
  return Number.isFinite(n) && DATE_FILTER_MONTH_SET.has(n);
}

function stringsFromCompsQueryParams(q: DealCompsQueryParams): FilterDraft {
  const str = (v: DealCompsQueryParams[string] | undefined) =>
    v !== undefined && v !== null && v !== "" ? String(v) : "";

  return {
    radiusKm: str(q.radius_km),
    monthsBack: str(q.months_back),
    loanAmountMin: str(q.loan_amount_min),
    loanAmountMax: str(q.loan_amount_max),
    salesPriceMin: str(q.sales_price_min),
    salesPriceMax: str(q.sales_price_max),
    salesAreaMin: str(q.sales_area_min),
    salesAreaMax: str(q.sales_area_max),
    salesRequireYield: q.sales_require_yield === true,
    salesSources: salesSourcesFromQuery(q),
    salesVendor: str(q.sales_vendor),
    salesPurchaser: str(q.sales_purchaser),
    leaseTotalRentMin: str(q.lease_total_rent_min),
    leaseTotalRentMax: str(q.lease_total_rent_max),
    leaseAreaMin: str(q.lease_area_min),
    leaseAreaMax: str(q.lease_area_max),
    limitPerType: str(q.limit_per_type),
  };
}

/** Draft from saved query; UI shows last 12 months when `months_back` was never saved. */
function initialFilterDraft(q: DealCompsQueryParams): FilterDraft {
  const base = stringsFromCompsQueryParams(q);
  if (!monthsBackSavedInQuery(q)) {
    return { ...base, monthsBack: "12" };
  }
  return base;
}

/** Parsed float fields for a draft; all floats come from one memoized pass. */
type ParsedFloats = {
  radiusKm: number | null;
  loanAmountMin: number | null;
  loanAmountMax: number | null;
  salesPriceMin: number | null;
  salesPriceMax: number | null;
  salesAreaMin: number | null;
  salesAreaMax: number | null;
  leaseTotalRentMin: number | null;
  leaseTotalRentMax: number | null;
  leaseAreaMin: number | null;
  leaseAreaMax: number | null;
};

function buildQueryParams(
  args: FilterDraft,
  floats: ParsedFloats,
  monthsBackTouched: boolean,
): DealCompsQueryParams {
  const out: DealCompsQueryParams = {};

  if (args.radiusKm.trim() === "") {
    out.radius_km = null;
  } else if (
    floats.radiusKm !== null &&
    floats.radiusKm >= RADIUS_KM_MIN &&
    floats.radiusKm <= RADIUS_KM_MAX
  ) {
    out.radius_km = floats.radiusKm;
  }

  if (monthsBackTouched) {
    if (args.monthsBack.trim() === "") {
      out.months_back = null;
    } else {
      const months = parsePositiveInt(args.monthsBack);
      if (months !== null && DATE_FILTER_MONTH_SET.has(months)) {
        out.months_back = months;
      }
    }
  }

  if (args.loanAmountMin.trim() === "") {
    out.loan_amount_min = null;
  } else if (
    floats.loanAmountMin !== null &&
    floats.loanAmountMin >= LOAN_AMOUNT_MIN_FLOOR &&
    floats.loanAmountMin <= LOAN_AMOUNT_MIN_CEIL
  ) {
    out.loan_amount_min = floats.loanAmountMin;
  }

  if (args.loanAmountMax.trim() === "") {
    out.loan_amount_max = null;
  } else if (floats.loanAmountMax !== null) {
    out.loan_amount_max = floats.loanAmountMax;
  }

  if (args.salesPriceMin.trim() === "") {
    out.sales_price_min = null;
  } else if (floats.salesPriceMin !== null) {
    out.sales_price_min = floats.salesPriceMin;
  }

  if (args.salesPriceMax.trim() === "") {
    out.sales_price_max = null;
  } else if (floats.salesPriceMax !== null) {
    out.sales_price_max = floats.salesPriceMax;
  }

  if (args.salesAreaMin.trim() === "") {
    out.sales_area_min = null;
  } else if (floats.salesAreaMin !== null) {
    out.sales_area_min = floats.salesAreaMin;
  }

  if (args.salesAreaMax.trim() === "") {
    out.sales_area_max = null;
  } else if (floats.salesAreaMax !== null) {
    out.sales_area_max = floats.salesAreaMax;
  }

  if (args.salesRequireYield) {
    out.sales_require_yield = true;
  } else {
    out.sales_require_yield = null;
  }

  if (args.salesSources.length === 0) {
    // Validation blocks persist; do not POST [].
  } else if (allSalesCompSourcesSelected(args.salesSources)) {
    out.sales_sources = null;
  } else {
    out.sales_sources = args.salesSources.filter(isSalesCompSourceId);
  }

  const salesVendor = args.salesVendor.trim();
  if (salesVendor === "") {
    out.sales_vendor = null;
  } else if (salesVendor.length <= SALES_NAME_FILTER_MAX_LENGTH) {
    out.sales_vendor = salesVendor;
  }

  const salesPurchaser = args.salesPurchaser.trim();
  if (salesPurchaser === "") {
    out.sales_purchaser = null;
  } else if (salesPurchaser.length <= SALES_NAME_FILTER_MAX_LENGTH) {
    out.sales_purchaser = salesPurchaser;
  }

  if (args.leaseTotalRentMin.trim() === "") {
    out.lease_total_rent_min = null;
  } else if (floats.leaseTotalRentMin !== null) {
    out.lease_total_rent_min = floats.leaseTotalRentMin;
  }

  if (args.leaseTotalRentMax.trim() === "") {
    out.lease_total_rent_max = null;
  } else if (floats.leaseTotalRentMax !== null) {
    out.lease_total_rent_max = floats.leaseTotalRentMax;
  }

  if (args.leaseAreaMin.trim() === "") {
    out.lease_area_min = null;
  } else if (floats.leaseAreaMin !== null) {
    out.lease_area_min = floats.leaseAreaMin;
  }

  if (args.leaseAreaMax.trim() === "") {
    out.lease_area_max = null;
  } else if (floats.leaseAreaMax !== null) {
    out.lease_area_max = floats.leaseAreaMax;
  }

  if (args.limitPerType.trim() === "") {
    out.limit_per_type = null;
  } else {
    const limit = parsePositiveInt(args.limitPerType);
    if (
      limit !== null &&
      limit >= LIMIT_PER_TYPE_MIN &&
      limit <= LIMIT_PER_TYPE_MAX
    ) {
      out.limit_per_type = limit;
    }
  }

  return out;
}

const DealCompFilter: React.FC<DealCompFilterProps> = ({
  salesAreaUnitLabel,
  onParamsChange,
  queryParams,
  onUserErrorsChange,
  clearFiltersRequested,
  setClearFiltersRequested,
  onClearFiltersClick,
}) => {
  const [monthsBackTouched, setMonthsBackTouched] = useState(() =>
    monthsBackSavedInQuery(queryParams),
  );

  const [draft, setDraft] = useState(() => initialFilterDraft(queryParams));

  const setDraftField = useCallback(
    (key: keyof FilterDraft, v: string | number) => {
      setDraft((d) => ({ ...d, [key]: String(v) }));
    },
    [],
  );

  const [debouncedFilterDraft] = useDebounce(draft, FILTER_DEBOUNCE_MS);

  const parsedFloats = useMemo((): ParsedFloats => {
    const d = debouncedFilterDraft;
    return {
      radiusKm: parsePositiveFloat(d.radiusKm),
      loanAmountMin: parsePositiveFloat(d.loanAmountMin),
      loanAmountMax: parsePositiveFloat(d.loanAmountMax),
      salesPriceMin: parsePositiveFloat(d.salesPriceMin),
      salesPriceMax: parsePositiveFloat(d.salesPriceMax),
      salesAreaMin: parseNonNegativeFloat(d.salesAreaMin),
      salesAreaMax: parseNonNegativeFloat(d.salesAreaMax),
      leaseTotalRentMin: parsePositiveFloat(d.leaseTotalRentMin),
      leaseTotalRentMax: parsePositiveFloat(d.leaseTotalRentMax),
      leaseAreaMin: parsePositiveFloat(d.leaseAreaMin),
      leaseAreaMax: parsePositiveFloat(d.leaseAreaMax),
    };
  }, [debouncedFilterDraft]);

  const builtParams = useMemo(() => {
    // Apply months_back from live draft on select (do not wait for debounce or blur).
    const argsForBuild: FilterDraft = {
      ...debouncedFilterDraft,
      monthsBack: draft.monthsBack,
    };
    return buildQueryParams(argsForBuild, parsedFloats, monthsBackTouched);
  }, [
    debouncedFilterDraft,
    draft.monthsBack,
    parsedFloats,
    monthsBackTouched,
  ]);

  useEffect(() => {
    if (!clearFiltersRequested) return;
    setDraft(initialFilterDraft({}));
    setMonthsBackTouched(false);
    setClearFiltersRequested(false);
  }, [clearFiltersRequested, setClearFiltersRequested]);

  const invalidRadius = useMemo(
    () =>
      debouncedFilterDraft.radiusKm.trim() !== "" &&
      (parsedFloats.radiusKm === null ||
        parsedFloats.radiusKm < RADIUS_KM_MIN ||
        parsedFloats.radiusKm > RADIUS_KM_MAX),
    [debouncedFilterDraft.radiusKm, parsedFloats.radiusKm],
  );

  const invalidMonths = useMemo(() => {
    if (debouncedFilterDraft.monthsBack.trim() === "") return false;
    const n = parsePositiveInt(debouncedFilterDraft.monthsBack);
    return n === null || !DATE_FILTER_MONTH_SET.has(n);
  }, [debouncedFilterDraft.monthsBack]);

  const invalidLoanMin = useMemo(
    () =>
      debouncedFilterDraft.loanAmountMin.trim() !== "" &&
      (parsedFloats.loanAmountMin === null ||
        parsedFloats.loanAmountMin < LOAN_AMOUNT_MIN_FLOOR ||
        parsedFloats.loanAmountMin > LOAN_AMOUNT_MIN_CEIL),
    [debouncedFilterDraft.loanAmountMin, parsedFloats.loanAmountMin],
  );

  const invalidLoanMax = useMemo(
    () =>
      debouncedFilterDraft.loanAmountMax.trim() !== "" &&
      parsedFloats.loanAmountMax === null,
    [debouncedFilterDraft.loanAmountMax, parsedFloats.loanAmountMax],
  );

  const invalidSalesMin = useMemo(
    () =>
      debouncedFilterDraft.salesPriceMin.trim() !== "" &&
      parsedFloats.salesPriceMin === null,
    [debouncedFilterDraft.salesPriceMin, parsedFloats.salesPriceMin],
  );

  const invalidSalesMax = useMemo(
    () =>
      debouncedFilterDraft.salesPriceMax.trim() !== "" &&
      parsedFloats.salesPriceMax === null,
    [debouncedFilterDraft.salesPriceMax, parsedFloats.salesPriceMax],
  );

  const invalidSalesAreaMin = useMemo(
    () =>
      debouncedFilterDraft.salesAreaMin.trim() !== "" &&
      parsedFloats.salesAreaMin === null,
    [debouncedFilterDraft.salesAreaMin, parsedFloats.salesAreaMin],
  );

  const invalidSalesAreaMax = useMemo(
    () =>
      debouncedFilterDraft.salesAreaMax.trim() !== "" &&
      parsedFloats.salesAreaMax === null,
    [debouncedFilterDraft.salesAreaMax, parsedFloats.salesAreaMax],
  );

  const invalidSalesAreaRange = useMemo(() => {
    const min = parsedFloats.salesAreaMin;
    const max = parsedFloats.salesAreaMax;
    if (min === null || max === null) return false;
    return min > max;
  }, [parsedFloats.salesAreaMin, parsedFloats.salesAreaMax]);

  const invalidSalesSources = useMemo(
    () => debouncedFilterDraft.salesSources.length === 0,
    [debouncedFilterDraft.salesSources],
  );

  const invalidSalesVendor = useMemo(() => {
    const t = debouncedFilterDraft.salesVendor.trim();
    return t !== "" && t.length > SALES_NAME_FILTER_MAX_LENGTH;
  }, [debouncedFilterDraft.salesVendor]);

  const invalidSalesPurchaser = useMemo(() => {
    const t = debouncedFilterDraft.salesPurchaser.trim();
    return t !== "" && t.length > SALES_NAME_FILTER_MAX_LENGTH;
  }, [debouncedFilterDraft.salesPurchaser]);

  const invalidRentMin = useMemo(
    () =>
      debouncedFilterDraft.leaseTotalRentMin.trim() !== "" &&
      parsedFloats.leaseTotalRentMin === null,
    [debouncedFilterDraft.leaseTotalRentMin, parsedFloats.leaseTotalRentMin],
  );

  const invalidRentMax = useMemo(
    () =>
      debouncedFilterDraft.leaseTotalRentMax.trim() !== "" &&
      parsedFloats.leaseTotalRentMax === null,
    [debouncedFilterDraft.leaseTotalRentMax, parsedFloats.leaseTotalRentMax],
  );

  const invalidAreaMin = useMemo(
    () =>
      debouncedFilterDraft.leaseAreaMin.trim() !== "" &&
      parsedFloats.leaseAreaMin === null,
    [debouncedFilterDraft.leaseAreaMin, parsedFloats.leaseAreaMin],
  );

  const invalidAreaMax = useMemo(
    () =>
      debouncedFilterDraft.leaseAreaMax.trim() !== "" &&
      parsedFloats.leaseAreaMax === null,
    [debouncedFilterDraft.leaseAreaMax, parsedFloats.leaseAreaMax],
  );

  const invalidLimit = useMemo(() => {
    if (debouncedFilterDraft.limitPerType.trim() === "") return false;
    const n = parsePositiveInt(debouncedFilterDraft.limitPerType);
    return n === null || n < LIMIT_PER_TYPE_MIN || n > LIMIT_PER_TYPE_MAX;
  }, [debouncedFilterDraft.limitPerType]);

  const hasUserErrors = useMemo(
    () =>
      invalidRadius ||
      invalidMonths ||
      invalidLoanMin ||
      invalidLoanMax ||
      invalidSalesMin ||
      invalidSalesMax ||
      invalidSalesAreaMin ||
      invalidSalesAreaMax ||
      invalidSalesAreaRange ||
      invalidSalesSources ||
      invalidSalesVendor ||
      invalidSalesPurchaser ||
      invalidRentMin ||
      invalidRentMax ||
      invalidAreaMin ||
      invalidAreaMax ||
      invalidLimit,
    [
      invalidRadius,
      invalidMonths,
      invalidLoanMin,
      invalidLoanMax,
      invalidSalesMin,
      invalidSalesMax,
      invalidSalesAreaMin,
      invalidSalesAreaMax,
      invalidSalesAreaRange,
      invalidSalesSources,
      invalidSalesVendor,
      invalidSalesPurchaser,
      invalidRentMin,
      invalidRentMax,
      invalidAreaMin,
      invalidAreaMax,
      invalidLimit,
    ],
  );

  useEffect(() => {
    onParamsChange(builtParams, hasUserErrors);
  }, [builtParams, hasUserErrors, onParamsChange]);

  useEffect(() => {
    onUserErrorsChange?.(hasUserErrors);
  }, [hasUserErrors, onUserErrorsChange]);

  return (
    <Box
      component="section"
      aria-label="Comps Filters"
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "flex-start",
        gap: { xs: 1.5, sm: 2 },
      }}
    >
      <Box
        sx={{
          flex: "1 1 auto",
          minWidth: 0,
          ...filterGroupsRowSx,
        }}
      >
        <FilterGroup label="General">
          <Box sx={fieldColumnSx}>
            <CustomTextInput
              label="KM to Deal Assets"
              type="number"
              formatNumber
              value={draft.radiusKm}
              margin="dense"
              fullWidth
              onChange={(v) => setDraftField("radiusKm", v)}
              redOutline={invalidRadius}
            />
            <FieldError message={invalidRadius ? "1–20000" : null} />
          </Box>
          <Box sx={fieldColumnSx}>
            <DealCompDateFilter
              value={draft.monthsBack}
              onChange={(v) => {
                setMonthsBackTouched(true);
                setDraftField("monthsBack", v);
              }}
              matchFilterFieldStyle
              invalid={invalidMonths}
              errorMessage={
                invalidMonths ? "12, 24, 36, or 48 months" : null
              }
            />
          </Box>
          <Box sx={fieldColumnSx}>
            <CustomTextInput
              label="Max No. Of Comps"
              type="number"
              formatNumber
              value={draft.limitPerType}
              margin="dense"
              fullWidth
              onChange={(v) => setDraftField("limitPerType", v)}
              redOutline={invalidLimit}
            />
            <FieldError message={invalidLimit ? "1–2000" : null} />
          </Box>
        </FilterGroup>

        <FilterGroup label="Lease">
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Lease Total Rent Min"
                type="number"
                formatNumber
                value={draft.leaseTotalRentMin}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("leaseTotalRentMin", v)}
                redOutline={invalidRentMin}
              />
              <FieldError message={invalidRentMin ? "Invalid" : null} />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Lease Total Rent Max"
                type="number"
                formatNumber
                value={draft.leaseTotalRentMax}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("leaseTotalRentMax", v)}
                redOutline={invalidRentMax}
              />
              <FieldError message={invalidRentMax ? "Invalid" : null} />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Lease Area Min"
                type="number"
                formatNumber
                value={draft.leaseAreaMin}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("leaseAreaMin", v)}
                redOutline={invalidAreaMin}
              />
              <FieldError message={invalidAreaMin ? "Invalid" : null} />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Lease Area Max"
                type="number"
                formatNumber
                value={draft.leaseAreaMax}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("leaseAreaMax", v)}
                redOutline={invalidAreaMax}
              />
              <FieldError message={invalidAreaMax ? "Invalid" : null} />
            </Box>
          </FilterGroup>

          <FilterGroup label="Sales">
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Sales Price Min"
                type="number"
                formatNumber
                value={draft.salesPriceMin}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("salesPriceMin", v)}
                redOutline={invalidSalesMin}
              />
              <FieldError message={invalidSalesMin ? "Invalid" : null} />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Sales Price Max"
                type="number"
                formatNumber
                value={draft.salesPriceMax}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("salesPriceMax", v)}
                redOutline={invalidSalesMax}
              />
              <FieldError message={invalidSalesMax ? "Invalid" : null} />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label={`Min area (${salesAreaUnitLabel})`}
                type="number"
                formatNumber
                value={draft.salesAreaMin}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("salesAreaMin", v)}
                redOutline={invalidSalesAreaMin || invalidSalesAreaRange}
              />
              <FieldError
                message={
                  invalidSalesAreaMin
                    ? "≥ 0"
                    : invalidSalesAreaRange
                      ? "Min ≤ max"
                      : null
                }
              />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label={`Max area (${salesAreaUnitLabel})`}
                type="number"
                formatNumber
                value={draft.salesAreaMax}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("salesAreaMax", v)}
                redOutline={invalidSalesAreaMax || invalidSalesAreaRange}
              />
              <FieldError
                message={
                  invalidSalesAreaMax
                    ? "≥ 0"
                    : invalidSalesAreaRange
                      ? "Max ≥ min"
                      : null
                }
              />
            </Box>
            <Box
              sx={{
                ...fieldColumnSx,
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                alignSelf: "stretch",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    disableRipple
                    size="small"
                    checked={draft.salesRequireYield}
                    onChange={(e) =>
                      setDraft((d) => ({
                        ...d,
                        salesRequireYield: e.target.checked,
                      }))
                    }
                    sx={{ color: "#1B7A63 !important", p: 0 }}
                  />
                }
                label="Require Yield"
                sx={{
                  m: 0,
                  alignItems: "center",
                  "& .MuiFormControlLabel-label": {
                    fontSize: "12px",
                    lineHeight: 1.25,
                    fontFamily: "Helvetica Neue, sans-serif",
                    color: "text.primary",
                  },
                }}
              />
            </Box>
            <Box sx={fieldColumnSx}>
              <Box
                sx={{
                  "& .custom-select": {
                    fontFamily: "Helvetica Neue, sans-serif !important",
                  },
                }}
              >
                <CustomMultiSelect
                  value={draft.salesSources}
                  onChange={(e) => {
                    const next = e.target.value;
                    setDraft((d) => ({
                      ...d,
                      salesSources:
                        typeof next === "string" ? next.split(",") : next,
                    }));
                  }}
                  options={SALES_COMP_SOURCE_OPTIONS}
                  labelKey="label"
                  placeholder="All sources"
                  fullWidth
                  heightOverride="32px"
                  fontSizeOverride="12px"
                  redOutline={invalidSalesSources}
                />
              </Box>
              <FieldError
                message={invalidSalesSources ? "Select at least one" : null}
              />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Vendor"
                type="text"
                value={draft.salesVendor}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("salesVendor", v)}
                redOutline={invalidSalesVendor}
              />
              <FieldError
                message={invalidSalesVendor ? "Max 500 characters" : null}
              />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Purchaser"
                type="text"
                value={draft.salesPurchaser}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("salesPurchaser", v)}
                redOutline={invalidSalesPurchaser}
              />
              <FieldError
                message={invalidSalesPurchaser ? "Max 500 characters" : null}
              />
            </Box>
          </FilterGroup>

          <FilterGroup label="Loans">
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Loan Amount Min"
                type="number"
                formatNumber
                value={draft.loanAmountMin}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("loanAmountMin", v)}
                redOutline={invalidLoanMin}
              />
              <FieldError message={invalidLoanMin ? "1M–120M" : null} />
            </Box>
            <Box sx={fieldColumnSx}>
              <CustomTextInput
                label="Loan Amount Max"
                type="number"
                formatNumber
                value={draft.loanAmountMax}
                margin="dense"
                fullWidth
                onChange={(v) => setDraftField("loanAmountMax", v)}
                redOutline={invalidLoanMax}
              />
              <FieldError message={invalidLoanMax ? "Invalid" : null} />
            </Box>
          </FilterGroup>
      </Box>

      <Box
        sx={{
          flex: "0 0 auto",
          alignSelf: "flex-start",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Button
          type="button"
          className="primary-button-clear"
          onClick={onClearFiltersClick}
          sx={{
            height: "32px",
            width: "max-content",
            minWidth: "max-content",
          }}
        >
          Clear Filters
        </Button>
      </Box>
    </Box>
  );
};

export default DealCompFilter;
